// "Get started" — guided onboarding for the WhatsApp Business Platform (Cloud
// API): what it is, what you need, how it works, what it costs, FAQ — with the
// apply button feeding straight into the Embedded Signup / existing-account
// connect flow (WhatsAppConnect) below.
import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ShieldCheck, Zap, Phone, Building2, UserCheck, ArrowRight, MessageCircle, BadgeCheck, ExternalLink } from "lucide-react";
import WhatsAppConnect from "./WhatsAppConnect";

const NEEDS = [
  { icon: UserCheck, title: "A Facebook account with admin access to your business", body: "You'll sign in with it and pick (or create) your Meta Business Portfolio during setup." },
  { icon: Phone, title: "A phone number for WhatsApp", body: "It must receive an SMS or voice call for the one-time code, and must NOT be active on the WhatsApp or WhatsApp Business app — delete the account there first, or use a fresh number. Landlines with voice verification work too." },
  { icon: Building2, title: "Your business details", body: "Legal business name, website and address, plus the display name customers will see. The display name should match your brand — Meta may review it." },
];

const STEPS = [
  { n: 1, title: "Continue with Facebook", body: "Opens Meta's official setup window. Sign in and allow DNexus CRM to manage WhatsApp for your business." },
  { n: 2, title: "Choose or create your business", body: "Select your Meta Business Portfolio, or create one on the spot with your business name." },
  { n: 3, title: "Create the WhatsApp Business Account", body: "Set the display name, category and description customers will see on your profile." },
  { n: 4, title: "Add & verify your number", body: "Enter the number and confirm the one-time code by SMS or call." },
  { n: 5, title: "Done — start messaging from the CRM", body: "We register the number, subscribe webhooks and open your inbox automatically. Nothing to configure." },
];

const FAQ = [
  { q: "Is the WhatsApp Business API really free?", a: "Yes — creating the WhatsApp Business Account and API access costs nothing, and DNexus adds no per-message markup. Meta bills you directly, per template message you send (marketing, utility, authentication) at the rate for the customer's country. Free-form replies inside the 24-hour customer service window are free." },
  { q: "Can I use my existing WhatsApp number?", a: "Yes, but a number can only be in one place. If it's currently on the WhatsApp or WhatsApp Business app, back up your chats, delete the account in the app (Settings → Account → Delete my account), wait a few minutes, then use it here. Your chat history from the app does not move to the API." },
  { q: "How long does approval take?", a: "API access is instant — the number is usable as soon as setup finishes. Display-name review can take up to a day or two if Meta flags it. Meta Business Verification (optional, for higher limits and the blue badge) usually takes 1–3 business days." },
  { q: "How many messages can I send?", a: "New accounts can start conversations with 250 unique customers per 24 hours. The limit rises automatically (1,000 → 10,000 → 100,000+) as you send quality messages and complete Business Verification. Replies to customers who message you first are not limited." },
  { q: "Can I keep using the WhatsApp app for this number?", a: "No — once a number is on the API it can only be used through the API (i.e. this CRM). Use a separate number if your team still needs the app." },
  { q: "What about the blue verified badge?", a: "The Official Business Account badge is granted by Meta to notable brands after Business Verification; it's optional and not required to send messages." },
];

function Faq({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-4 py-3 text-left text-sm font-medium text-slate-800">
        {item.q}
        <span className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}><ArrowRight className="h-4 w-4" /></span>
      </button>
      {open && <p className="pb-3 text-sm leading-relaxed text-slate-600">{item.a}</p>}
    </div>
  );
}

export default function WhatsAppGetStarted({ currentUser, businessId, onConnected, connectedCount = 0, hasLegacy = false, onViewLegacy }) {
  const [manualOpen, setManualOpen] = useState(false);
  const scrollToConnect = () => document.getElementById("wa-connect")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="space-y-6" data-testid="wa-get-started">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6 md:p-10">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <ShieldCheck className="h-3.5 w-3.5" /> Official Meta WhatsApp Business Platform · Cloud API
            </span>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
              Set up your <span className="text-emerald-700">FREE</span> WhatsApp Business Account
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-[15px]">
              Apply for the WhatsApp Business API in a few minutes, directly with Meta — no middleman, no markup.
              The account is free; Meta only bills per template message you send, and replies inside the 24-hour
              customer window cost nothing. Your inbox, live chat, templates and broadcasts all run from this CRM.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => { setManualOpen(false); scrollToConnect(); }} data-testid="wa-apply-cta"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700">
                <Zap className="h-4 w-4" /> Apply for WhatsApp Business API
              </button>
              <button type="button" onClick={() => { setManualOpen(true); scrollToConnect(); }} data-testid="wa-have-account-cta"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 hover:border-slate-300">
                I already have API access
              </button>
            </div>
            {connectedCount > 0 && (
              <p className="mt-3 text-xs text-emerald-700"><BadgeCheck className="mr-1 inline h-3.5 w-3.5" />{connectedCount} number{connectedCount === 1 ? " is" : "s are"} already connected — use the buttons above to add another.</p>
            )}
            {hasLegacy && onViewLegacy && (
              <button type="button" onClick={onViewLegacy} className="mt-3 text-xs font-medium text-slate-500 underline-offset-2 hover:underline">View conversation history from your previous connection</button>
            )}
          </div>
          <div className="rounded-2xl border border-white/60 bg-white/80 p-5 shadow-sm backdrop-blur">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">What does it cost?</div>
            <dl className="mt-3 space-y-2.5 text-sm">
              <div className="flex items-center justify-between"><dt className="text-slate-600">WhatsApp Business Account</dt><dd className="font-semibold text-emerald-700">Free</dd></div>
              <div className="flex items-center justify-between"><dt className="text-slate-600">API access & setup</dt><dd className="font-semibold text-emerald-700">Free</dd></div>
              <div className="flex items-center justify-between"><dt className="text-slate-600">Replies within 24h window</dt><dd className="font-semibold text-emerald-700">Free</dd></div>
              <div className="flex items-center justify-between"><dt className="text-slate-600">Template messages</dt><dd className="font-semibold text-slate-800">Meta's country rate</dd></div>
              <div className="flex items-center justify-between"><dt className="text-slate-600">DNexus per-message markup</dt><dd className="font-semibold text-emerald-700">None</dd></div>
            </dl>
            <a href="https://developers.facebook.com/docs/whatsapp/pricing" target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
              Meta's official rate card <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Before you start */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Before you start, have these ready</h3>
        <div className="grid gap-3 md:grid-cols-3">
          {NEEDS.map((n) => (
            <div key={n.title} className="rounded-2xl border border-slate-200 bg-white p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><n.icon className="h-4 w-4" /></span>
              <div className="mt-3 text-sm font-semibold text-slate-800">{n.title}</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{n.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900">How it works</h3>
        <ol className="mt-4 grid gap-4 md:grid-cols-5">
          {STEPS.map((s) => (
            <li key={s.n} className="relative">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">{s.n}</span>
                <span className="text-sm font-semibold text-slate-800">{s.title}</span>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{s.body}</p>
            </li>
          ))}
        </ol>
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
          <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          Optional afterwards: complete <b className="mx-1">Meta Business Verification</b> in Business Manager for higher sending limits and badge eligibility.
        </p>
      </section>

      {/* Connect */}
      <section id="wa-connect" className="scroll-mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
        <WhatsAppConnect key={manualOpen ? "manual" : "apply"} currentUser={currentUser} businessId={businessId} onConnected={onConnected} initialManualOpen={manualOpen} />
      </section>

      {/* FAQ */}
      <section className="rounded-2xl border border-slate-200 bg-white px-5 py-2">
        <h3 className="pt-3 text-sm font-semibold text-slate-900">Frequently asked</h3>
        <div className="mt-1">{FAQ.map((f) => <Faq key={f.q} item={f} />)}</div>
      </section>

      <p className="text-center text-xs text-slate-400">
        <MessageCircle className="mr-1 inline h-3.5 w-3.5" />
        Already connected? Head to the <Link to="/whatsapp" className="font-medium text-emerald-700 hover:underline">Inbox</Link>.
      </p>
    </div>
  );
}
